import { EXPLORE_ROUTES } from "./exploreRoutes";
import { DASHBOARD_ROUTES } from "./dashboardRoutes";
import { SETTINGS_ROUTES } from "../settingProfile/settingsRoutes";
import { SUBSCRIPTION_ROUTES } from "./subscription/subscriptionRoutes";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import  "../layout/Layout.css";
import LogoIcon from "../assets/images/11.png";
import { useAppBranding } from "../hooks/useAppBranding";

const INTERNAL_ROUTE_MAP = {
  dashboard: DASHBOARD_ROUTES.home,
  states: DASHBOARD_ROUTES.states,
  exams: EXPLORE_ROUTES.exams,
  favoriteStates: DASHBOARD_ROUTES.states,
  favoriteExams: DASHBOARD_ROUTES.favoriteExams,
  resetPreviousContent: DASHBOARD_ROUTES.home,
  changePassword: SETTINGS_ROUTES.password,
  editProfile: SETTINGS_ROUTES.profile,
  premium: SUBSCRIPTION_ROUTES.list,
  restorePayment: "/pricing",
  contentLanguage: SETTINGS_ROUTES.contentLanguage,
  appLanguage: SETTINGS_ROUTES.appLanguage,
  aboutUS: "/about-us",
  contactUS: "/contact",
  reviews: "/reviews",
  termsConditions: "/terms-and-conditions",
  privacyPolicy: "/privacy-policy",
  notification: SETTINGS_ROUTES.notifications,
};

function normalizeSections(menuList = []) {
  return [...menuList].sort((left, right) => (left?.order || 0) - (right?.order || 0));
}

function normalizeItems(items = []) {
  return [...items].sort((left, right) => (left?.order || 0) - (right?.order || 0));
}

function getInitials(name) {
  return (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
}

function resolveMenuAction(item) {
  if (item?.key === "logOut") {
    return {
      type: "logout",
    };
  }

  if (item?.menu_type === "E" && item?.external_link) {
    return {
      type: "external",
      href: item.external_link,
    };
  }

  if (item?.external_link && /^https?:\/\//i.test(item.external_link)) {
    return {
      type: "external",
      href: item.external_link,
    };
  }

  if (INTERNAL_ROUTE_MAP[item?.key]) {
    return {
      type: "internal",
      href: INTERNAL_ROUTE_MAP[item.key],
    };
  }

  return {
    type: "disabled",
  };
}

const ACTION_MENU_KEYS = new Set(['resetPreviousContent', 'restorePayment']);
const normalizePath = path => (String(path || '').replace(/\/+$/, '') || '/').toLowerCase();

export function findActiveSidebarItem(sections, location) {
  const pathname = normalizePath(location.pathname);
  let selected = null;
  let selectedScore = -1;
  for (const section of sections) {
    for (const item of normalizeItems(section.items)) {
      const action = resolveMenuAction(item);
      if (action.type !== 'internal' || ACTION_MENU_KEYS.has(item.key)) continue;
      const paths = [action.href,
        ...(item.key === 'dashboard' ? [DASHBOARD_ROUTES.progress] : []),
        ...(item.key === 'exams' ? [DASHBOARD_ROUTES.examCategories, '/exam-courses'] : []),
      ];
      for (const path of paths) {
        const normalized = normalizePath(path);
        const matches = pathname === normalized || (normalized !== DASHBOARD_ROUTES.home && pathname.startsWith(`${normalized}/`));
        if (!matches) continue;
        // Shared destinations have one winner; an explicit sidebar selection
        // distinguishes Favorite States from the main States entry.
        const score = normalized.length * 10 + (item.key === 'states' ? 1 : 0) +
          (location.state?.sidebarMenuKey === item.key ? 2 : 0);
        if (score > selectedScore) { selected = item; selectedScore = score; }
      }
    }
  }
  return selected;
}

const Sidebar = ({ isOpen, onClose, onLogoutClick, sidebarData, sidebarError, sidebarLoading }) => {
  const location = useLocation();
  const { data: appBranding } = useAppBranding();
  const profile = sidebarData?.user_data || {};
  const sections = normalizeSections(sidebarData?.menu_list);
  const activeItem = findActiveSidebarItem(sections, location);
  const userName = profile?.name || "User";
  const userEmail = profile?.email || "";
  const userAvatar = profile?.user_profile || "";
  const sidebarLogo = appBranding?.app_logo_rectangle || appBranding?.app_logo_square || LogoIcon;

  return (
    <aside className={`sidebar ${isOpen ? "open" : ""}`}>
      <div className="brand-header">
        <Link to={DASHBOARD_ROUTES.home} className="brand-logo">
          <img
            src={sidebarLogo}
            alt="Brand logo"
            className="brand-logo__icon"
          />
        </Link>

        
  {/* Mobile Close Button */}
  <button
    type="button"
    className="sidebar-close-btn"
    onClick={onClose}
    aria-label="Close sidebar"
  >
    <i className="bi bi-x-lg"></i>
  </button>
      </div>

      <Link to={SETTINGS_ROUTES.profile} className="homeuser-profile">
        <div className="homeuser-profile__avatar">
          {userAvatar ? (
            <img src={userAvatar} alt={userName} className="homeuser-profile__avatar-image" />
          ) : (
            <span className="homeuser-profile__avatar-text">{getInitials(userName) || "VK"}</span>
          )}
        </div>

        <div className="homeuser-profile__content">
          <h4 className="homeuser-profile__name">{userName}</h4>
          <p className="homeuser-profile__email">{userEmail}</p>
        </div>
      </Link>

      <div className="sidebar-menu home-sidebar">
        {sidebarLoading ? <p className="home-title mb-0">Loading menu...</p> : null}
        {sidebarError ? <p className="home-title mb-0">{sidebarError}</p> : null}

        {!sidebarLoading && !sidebarError
          ? sections.map((section) => (
              <div key={section.id || section.key}>
                <p className="home-title mb-0">{section.display_value}</p>
                <ul className="mb-0">
                  {normalizeItems(section.items).map((item) => {
                    const action = resolveMenuAction(item);
                    const isActive = item === activeItem;

                    return (
                      <li
                        key={item.id || item.key}
                        className={`${isActive ? "active" : ""}${action.type === "disabled" ? " is-disabled" : ""}`}
                      >
                        {action.type === "internal" ? (
                          <Link to={action.href}
                            state={item.key === 'favoriteStates' ? { sidebarMenuKey: item.key } : undefined}
                            aria-current={isActive ? 'page' : undefined}>
                            <span className="home-sidebar__icon" aria-hidden="true">
                              {item.image_url ? <img src={item.image_url} alt="" /> : <i className="fa-solid fa-house"></i>}
                            </span>
                            <span>{item.display_value}</span>
                          </Link>
                        ) : null}

                        {action.type === "external" ? (
                          <a href={action.href} rel="noreferrer" target="_blank">
                            <span className="home-sidebar__icon" aria-hidden="true">
                              {item.image_url ? <img src={item.image_url} alt="" /> : <i className="fa-solid fa-arrow-up-right-from-square"></i>}
                            </span>
                            <span>{item.display_value}</span>
                          </a>
                        ) : null}

                        {action.type === "logout" ? (
                          <a
                            href="#"
                            className="home-sidebar__button"
                            onClick={(event) => {
                              event.preventDefault();
                              onLogoutClick();
                            }}
                          >
                            <span className="home-sidebar__icon" aria-hidden="true">
                              {item.image_url ? <img src={item.image_url} alt="" /> : <i className="fa-solid fa-right-from-bracket"></i>}
                            </span>
                            <span>{item.display_value}</span>
                          </a>
                        ) : null}

                        {action.type === "disabled" ? (
                          <Link to="#" onClick={(event) => event.preventDefault()}>
                            <span className="home-sidebar__icon" aria-hidden="true">
                              {item.image_url ? <img src={item.image_url} alt="" /> : <i className="fa-solid fa-house"></i>}
                            </span>
                            <span>{item.display_value}</span>
                          </Link>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          : null}
      </div>
    </aside>
  );
};

export default Sidebar;

